import {
  CUSTOM_ELEMENTS_SCHEMA,
  ModuleWithProviders,
  NgModule,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { ErrorLabelComponent } from "./components/error-label/error-label.component";
import { RouterModule } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { SeparatorComponent } from "./components/separator/separator.component";
import { MessageViewerComponent } from "./components/message-viewer/message-viewer.component";
import { MessageService } from "./services/message.service";
import { OrderConverter } from "../orders/converters/order.converter";
import { OrdersResearchConverter } from "../orders/converters/orders-research.converter";
import { OrdersResearchOptionsConverter } from "../orders/converters/orders-research-options.converter";
import { OrdersResearchFilterConverter } from "../orders/converters/orders-research-filter.converter";
import { PaginationConverter } from "../orders/converters/pagination.converter";
import { ClientAccountChoiceComponent } from "./components/client-account-choice/client-account-choice.component";
import { ClientAccountService } from "./services/client-account.service";
import { AdvancedSearchBarComponent } from "./components/advanced-search-bar/advanced-search-bar.component";
import { AngularMyDatePickerModule } from "angular-mydatepicker";
import { ClonerService } from "../core/services/clone.service";
import { PageNotFoundComponent } from "./components/page-not-found/page-not-found.component";
import { ProductNotSupportedComponent } from "./components/product-not-supported/product-not-supported.component";
import { EnrollmentButtonComponent } from "./components/enrollment-button/enrollment-button.component";
import { RecognitionButtonComponent } from "./components/recognition-button/recognition-button.component";
import { RestartButtonComponent } from "./components/restart-button/restart-button.component"
import { NgxPaginationModule } from "ngx-pagination";
import { AgreementConverter } from "../orders/converters/agreement.converter";
import { BasicProductConverter } from "../orders/converters/basic-product.converter";
import { ContactConverter } from "../orders/converters/contact.converter";
import { CpaConverter } from "../orders/converters/cpa.converter";
import { ExtensionConverter } from "../orders/converters/extension.converter";
import { IdentificationConverter } from "../orders/converters/identification.converter";
import { IdentityConverter } from "../orders/converters/identity.converter";
import { ProductConverter } from "../orders/converters/product.converter";
import { ProductTypeConverter } from "../orders/converters/product-type.converter";
import { PurchaseConverter } from "../orders/converters/purchase.converter";
import { ResidenceConverter } from "../orders/converters/residence.converter";
import { SecurityInfoConverter } from "../orders/converters/security-info.converter";
import { SecurityQuestionConverter } from "../orders/converters/security-question.converter";
import { ServiceConverter } from "../orders/converters/service.converter";
import { ServicesCombinationConverter } from "../orders/converters/service-combination.converter";
import { SubjectConverter } from "../orders/converters/subject.converter";
import { OrderFileConverter } from "../orders/converters/order-file.converter";
import { ArticleConverter } from "../orders/converters/article.converter";
import { CpaPriceConverter } from "../orders/converters/cpa-price.converter";
import { DurationConverter } from "../orders/converters/duration.converter";
import { ProductCompositionConverter } from "../orders/converters/product-composition.converter";
import { PropertyConverter } from "../orders/converters/property.converter";
import { RequestConverter } from "../orders/converters/request.converter";
import { EnrichmentConverter } from "../orders/converters/enrichment.converter";
import { CustomerConverter } from "./converters/customer.converter";
import { AccountConverter } from "./converters/account.converter";
import { DomainConverter } from "./converters/domain.converter";
import { SpinnerComponent } from "./components/spinner/spinner.component";
import { ContactInfocertComponent } from "./components/contact-infocert/contact-infocert.component";
import { BasicProperty1Converter } from "../orders/converters/properties.converter";
import { IdCardConverter } from "../orders/converters/idcard.converter";
import { UsersService } from "./services/users.service";
import { UniquePipe } from "./pipe/unique.pipe";
import { GroupByPipe } from "./pipe/group-by.pipe";
import { OrderByPipe } from './pipe/order-by.pipe';
import { ApplicationUserConverter } from "./converters/application-user.converter";
import { AccountingConverter } from "./converters/accounting.converter";
import { AggregationConverter } from "./converters/aggregation.converter";
import { RoleConverter } from "./converters/role.converter";
import { UnitConverter } from "./converters/unit.converter";
import { SettingService } from "./services/setting.service";
import { SettingModalComponent } from "./components/setting/setting-modal.component";
import { UserChoiceComponent } from "./components/user-choice/user-choice.component";
import { ModalService } from "./services/modal.service";
import { SettingComponent } from "./components/setting/setting-component/setting-component.component";
import { TreeUnitResearchConverter } from "./converters/tree-unit-research.converter";
import { TreeUnitResearchFilterConverter } from "./converters/tree-unit-research-filter.converter";
import { TreeUnitResearchOptionsConverter } from "./converters/tree-unit-research-options.converter";
import { TreeComponent } from "./components/tree/tree.component";
import { SettingLayoutComponent } from "./components/setting/setting-layout/setting-layout.component";
import { DraftConverter } from "../orders/converters/draft.converter";
import { DraftOperatorConverter } from "../orders/converters/draft-operator.converter";
import { DraftsResearchConverter } from "../orders/converters/drafts-research.converter";
import { DraftsResearchFilterConverter } from "../orders/converters/drafts-research-filter.converter";
import { DraftsResearchFilter1Converter } from "../orders/converters/drafts-research-filter1.converter";
import { ModifyDraftConverter } from "../orders/converters/modify-draft.converter";
import { CreateDraftConverter } from "../orders/converters/create-draft.converter";
import { SettingConverter } from "./converters/setting.converter";
import { LayoutSettingConverter } from "./converters/layout-setting.converter";
import { GlobalSettingConverter } from "./converters/global-setting.converter";
import { FeaturesAuthDirective } from "./directives/features-auth.directive";
import { DragDropDirective } from "./directives/dragDrop.directive";
import { CreatedOrdersResponseConverter } from "../orders/converters/created-orders-response.converter";
import { CreatedOrderConverter } from "../orders/converters/created-order.converter";
import { NotCreatedOrderConverter } from "../orders/converters/not-created-order.converter";
import { ExternalErrorConverter } from "./converters/external-error.converter";
import { EobiErrorConverter } from "./converters/eobi-error.converter";
import { ServiceHistoryConverter } from "../orders/converters/service-history.converter";
import { FilterConverter } from "./converters/filter.converter";
import { ModifyProductStateConverter } from "../orders/converters/modifyProductState.converter";
import { ProductNoteConverter } from "../orders/converters/product-note.converter";
import { UnitPropertiesConverter } from "./converters/unit-properties.converter";
import { InfoBoxComponent } from "./components/info-box/info-box.component";
import { DataForReviewConverter } from "./converters/dataForReview.converter";
import { TransictionsConverter } from "../orders/converters/transictions.converter";
import { TransictionStateConverter } from "../orders/converters/transiction-state.converter";
import { ProcessToConverter } from "../orders/converters/process-to.converter";
import { StateChangeToConverter } from "../orders/converters/state-change-to.converter";
import { CustomerPropertyConverter } from "../orders/converters/customer-property.converter";
import { RoleStateChangeToConverter } from "../orders/converters/role-state-change-to.converter";
import { TypeStateChangeToConverter } from "../orders/converters/type-state-change-to.converter";
import { AdditionalTypeStateChangeToConverter } from "../orders/converters/additional-type-state-change-to.converter";
import { RoleProcessToConverter } from "../orders/converters/role-process-to.converter";
import { RecognitionFlowInfoBoxComponent } from "./components/info-box/recognition-flow-info-box/recognition-flow-info-box.component";
import { RequestFlowInfoBoxComponent } from "./components/info-box/request-flow-info-box/request-flow-info-box.component";
import { WarpService } from "./services/warp.service";
import { UploadFileComponent } from "./components/upload-file/upload-file.component";
import { UnauthorizedComponent } from "./errors/unauthorized/unauthorized.component";
import { LoginComponent } from "./components/auth/login/login.component";
import { LoadingComponent } from "./components/auth/loading/loading.component";
import { ProcessCompletedComponent } from "./components/auth/process-completed/process-completed.component";
// warp import
import "@infocert/warp-frontend/packages/warp-app/WarpStory";
import { ArticlesService } from "@app/orders/services/articles.service";
import { OrdersService } from "@app/orders/services/orders.service";
import { ProductService } from "@app/orders/services/product.service";
import { ModalComponent } from "./components/modal/modal.component";
import { ChoosePassphraseComponent } from "./components/choose-passphrase/choose-passphrase.component";
import { SpinnerDirective } from "./directives/spinner.directive";
import { ChooseIdentificationTypeComponent } from "@app/worm-components/components/choose-identification-type/choose-identification-type.component";
import { WormChoosePassphraseComponent } from "@app/worm-components/components/worm-choose-passphrase/worm-choose-passphrase.component";
import { SanitizeUrlPipe } from './pipe/sanitize-url.pipe';
import { IsFeatureFlagEnabledDirective } from './directives/is-feature-flag-enabled.directive';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { NgxPrintModule } from 'ngx-print';

@NgModule({
  declarations: [
    LoadingComponent,
    ProcessCompletedComponent,
    LoginComponent,
    ErrorLabelComponent,
    PageNotFoundComponent,
    ProductNotSupportedComponent,
    SeparatorComponent,
    MessageViewerComponent,
    ClientAccountChoiceComponent,
    AdvancedSearchBarComponent,
    SpinnerComponent,
    ContactInfocertComponent,
    UniquePipe,
    GroupByPipe,
    SettingModalComponent,
    UserChoiceComponent,
    SettingComponent,
    TreeComponent,
    SettingLayoutComponent,
    FeaturesAuthDirective,
    DragDropDirective,
    InfoBoxComponent,
    RecognitionFlowInfoBoxComponent,
    RequestFlowInfoBoxComponent,
    UploadFileComponent,
    ModalComponent,
    ChoosePassphraseComponent,
    ChooseIdentificationTypeComponent,
    WormChoosePassphraseComponent,
    SpinnerDirective,
    GroupByPipe,
    OrderByPipe,
    UnauthorizedComponent,
    EnrollmentButtonComponent,
    RecognitionButtonComponent,
    RestartButtonComponent,
    SanitizeUrlPipe,
    IsFeatureFlagEnabledDirective
  ],
  exports: [
    ErrorLabelComponent,
    PageNotFoundComponent,
    ProductNotSupportedComponent,
    SeparatorComponent,
    MessageViewerComponent,
    ClientAccountChoiceComponent,
    AdvancedSearchBarComponent,
    SpinnerComponent,
    ContactInfocertComponent,
    SettingModalComponent,
    FeaturesAuthDirective,
    DragDropDirective,
    InfoBoxComponent,
    UploadFileComponent,
    ModalComponent,
    ChoosePassphraseComponent,
    ChooseIdentificationTypeComponent,
    WormChoosePassphraseComponent,
    UnauthorizedComponent,
    //module
    CommonModule,
    TranslateModule,
    FormsModule,
    ReactiveFormsModule,
    AngularMyDatePickerModule,
    NgxPaginationModule,
    UniquePipe,
    GroupByPipe,
    OrderByPipe,
    SanitizeUrlPipe,
    SpinnerDirective,
    EnrollmentButtonComponent,
    RecognitionButtonComponent,
    RestartButtonComponent,
    IsFeatureFlagEnabledDirective,
    PdfViewerModule,
    NgxPrintModule
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TranslateModule,
    AngularMyDatePickerModule,
    NgxPaginationModule,
    RouterModule,
    PdfViewerModule,
    NgxPrintModule
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class SharedModule {
  constructor() { }

  static forRoot(): ModuleWithProviders<SharedModule> {
    return {
      ngModule: SharedModule,
      providers: [
        MessageService,
        ClientAccountService,
        ClonerService,
        UsersService,
        SettingService,
        ModalService,
        WarpService,
        //converters
        OrderConverter,
        ArticlesService,
        OrdersService,
        ProductService,
        OrdersResearchConverter,
        OrdersResearchOptionsConverter,
        OrdersResearchFilterConverter,
        PaginationConverter,
        AgreementConverter,
        BasicProductConverter,
        ContactConverter,
        CpaConverter,
        ExtensionConverter,
        IdentificationConverter,
        IdentityConverter,
        ProductConverter,
        ProductTypeConverter,
        PurchaseConverter,
        ResidenceConverter,
        SecurityInfoConverter,
        SecurityQuestionConverter,
        ServiceConverter,
        ServicesCombinationConverter,
        SubjectConverter,
        OrderFileConverter,
        ArticleConverter,
        CpaPriceConverter,
        DurationConverter,
        ProductCompositionConverter,
        PropertyConverter,
        RequestConverter,
        EnrichmentConverter,
        CustomerConverter,
        AccountConverter,
        DomainConverter,
        BasicProperty1Converter,
        IdCardConverter,
        ApplicationUserConverter,
        AccountingConverter,
        AggregationConverter,
        RoleConverter,
        UnitConverter,
        UnitPropertiesConverter,
        TreeUnitResearchConverter,
        TreeUnitResearchFilterConverter,
        TreeUnitResearchOptionsConverter,
        DraftConverter,
        DraftOperatorConverter,
        DraftsResearchConverter,
        DraftsResearchFilterConverter,
        DraftsResearchFilter1Converter,
        ModifyDraftConverter,
        CreateDraftConverter,
        SettingConverter,
        LayoutSettingConverter,
        GlobalSettingConverter,
        CreatedOrdersResponseConverter,
        CreatedOrderConverter,
        NotCreatedOrderConverter,
        ExternalErrorConverter,
        EobiErrorConverter,
        ServiceHistoryConverter,
        FilterConverter,
        ModifyProductStateConverter,
        ProductNoteConverter,
        DataForReviewConverter,
        TransictionsConverter,
        TransictionStateConverter,
        ProcessToConverter,
        StateChangeToConverter,
        CustomerPropertyConverter,
        RoleStateChangeToConverter,
        TypeStateChangeToConverter,
        AdditionalTypeStateChangeToConverter,
        RoleProcessToConverter,
      ],
    };
  }
}
